import React, { useState, useEffect, useRef } from 'react';
import PhoneInput from 'react-phone-number-input'
import 'react-phone-number-input/style.css'
import {
    FormControl,
    FormLabel,
    Input,
    Select,
    Textarea,
    HStack,
    VStack,
    Text,
    Box,
    Alert,
    AlertIcon,
    Spinner,
    Center,
    Button,
    IconButton,
    useToast,
    Badge,
    Accordion,
    AccordionItem,
    AccordionButton,
    AccordionPanel,
    AccordionIcon,
    Icon,
    SimpleGrid,
} from '@chakra-ui/react';
import { supabase } from './utils/supabaseClient';
import { DeleteIcon, AddIcon } from '@chakra-ui/icons';
import { Select as SearchableSelect } from "chakra-react-select";
import countryList from 'react-select-country-list';
import { FileAttachmentRow } from './components/FileAttachmentRow';

const AdditionalFields = ({ 
    formData, 
    setFormData, 
    validationErrors, 
    setValidationErrors, 
    registrationId, 
    setRegistrationId, 
    isReadOnly = false, 
    onAutoSave,
    selectedFiles,
    setSelectedFiles,
    uploadingFiles,
    setUploadingFiles
}) => {
    const [additionalFields, setAdditionalFields] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [isCreatingDraft, setIsCreatingDraft] = useState(false);
    const [activeOtherFields, setActiveOtherFields] = useState(new Set());
    // Track which fields have been touched (focused and blurred)
    const [touchedFields, setTouchedFields] = useState(new Set());
    
    // Get country list
    const countryOptions = countryList().getData();
    // Cache a stable folder path for full certification uploads per session
    const fullCertFolderPathRef = useRef(null);
    const toast = useToast();

    // Section configuration - based on database sections
    const getSectionConfig = (sectionName) => {
        const configs = {
            'Compulsory fields': {
                icon: '⭐',
                color: 'red',
                bgColor: 'red.50',
                borderColor: 'red.200'
            },
            'Personal Details': {
                icon: '👤',
                color: 'green',
                bgColor: 'green.50',
                borderColor: 'green.200'
            },
            'Education': {
                icon: '🎓',
                color: 'purple',
                bgColor: 'purple.50',
                borderColor: 'purple.200'
            },
            'Work': {
                icon: '💼',
                color: 'orange',
                bgColor: 'orange.50',
                borderColor: 'orange.200'
            },
            'Programme Selection': {
                icon: '📚',
                color: 'blue',
                bgColor: 'blue.50',
                borderColor: 'blue.200'
            },
            'Additional Information': {
                icon: '📝',
                color: 'gray',
                bgColor: 'gray.50',
                borderColor: 'gray.200'
            }
        };
        return configs[sectionName] || configs['Additional Information'];
    };

    // Use the database section directly - no hardcoded mapping needed
    const mapToSimplifiedSection = (originalSection) => {
        // Use the section from the database directly
        return originalSection || 'Additional Information';
    };

    useEffect(() => {
        fetchAdditionalFields();
    }, []);

    // Re-validate fields with conditional requirements when formData changes
    useEffect(() => {
        if (additionalFields.length === 0) return;
        
        // Get all trigger field keys that could affect conditional requirements
        const triggerFields = new Set();
        additionalFields.forEach(field => {
            if (field.conditional_requirements?.required_when?.field) {
                triggerFields.add(field.conditional_requirements.required_when.field);
            }
        });

        // Re-validate all fields that have conditional requirements
        const fieldsToRevalidate = additionalFields.filter(field => 
            field.conditional_requirements && 
            field.conditional_requirements.required_when
        );

        setValidationErrors(prev => {
            const newErrors = { ...prev };
            let hasChanges = false;

            fieldsToRevalidate.forEach(field => {
                // Check if field should be visible
                const isVisible = shouldShowField(field);
                
                // If field is not visible, clear its validation error
                if (!isVisible && prev[field.field_key]) {
                    newErrors[field.field_key] = false;
                    hasChanges = true;
                    return;
                }
                
                // Only validate visible fields
                if (isVisible) {
                    const fieldValue = getFieldValue(field.field_key);
                    const error = validateField(field, fieldValue);
                    const currentError = prev[field.field_key];
                    
                    // Only update if error state changed
                    if (error !== currentError) {
                        newErrors[field.field_key] = error || false;
                        hasChanges = true;
                    }
                }
            });

            return hasChanges ? newErrors : prev;
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [formData]); // Only depend on formData - additionalFields is stable after initial load

    // Create draft registration for full certifications (needed for file uploads)
    const createDraftRegistration = async () => {
        if (registrationId || isCreatingDraft) return;
        
        if (!formData.participant_name || formData.participant_name.trim() === '') {
            return;
        }

        // Check if this is a full certification registration
        const urlParams = new URLSearchParams(window.location.search);
        const isFullCertification = urlParams.get('full_certification');
        const fullCertificationId = urlParams.get('full_certification');
        const certificationRunId = urlParams.get('certification_run_id');
        
        // Only create draft for full certifications (individual courses don't need it)
        if (!isFullCertification || !fullCertificationId || !certificationRunId) {
            return;
        }

        setIsCreatingDraft(true);
        try {
            const draftData = {
                participant_name: formData.participant_name,
                registration_stage: "draft",
                // Add full certification context for consistent folder naming
                full_certification_id: fullCertificationId,
                certification_run_id: certificationRunId,
                participant_email: formData.participant_email || null
            };

            const { data, error } = await supabase
                .from('registration')
                .insert([draftData])
                .select("id")
                .single();

            if (error) {
                console.error('Error creating draft registration:', error);
                toast({
                    title: "Error creating registration",
                    description: "Please try again later",
                    status: "error",
                    duration: 3000,
                    isClosable: true,
                });
            } else {
                setRegistrationId(data.id);
                // Silent success - no toast message
            }
        } catch (error) {
            console.error('Error creating draft registration:', error);
        } finally {
            setIsCreatingDraft(false);
        }
    };

    // Watch for participant name changes (only for full certifications)
    useEffect(() => {
        const urlParams = new URLSearchParams(window.location.search);
        const isFullCertification = urlParams.get('full_certification');
        
        if (isFullCertification && formData.participant_name && formData.participant_name.trim() !== '' && !registrationId) {
            createDraftRegistration();
        }
    }, [formData.participant_name, registrationId]);

    // Helper function to get nested field value from formData
    const getNestedValue = (data, fieldKey) => {
        // First check if it's a direct key
        if (data[fieldKey] !== undefined && data[fieldKey] !== null && data[fieldKey] !== '') {
            return data[fieldKey];
        }
        
        // Then check for nested keys (e.g., "personal_details.gender")
        if (fieldKey.includes('.')) {
            const keys = fieldKey.split('.');
            let current = data;
            for (const key of keys) {
                if (current && typeof current === 'object' && key in current) {
                    current = current[key];
                } else {
                    return '';
                }
            }
            return current || '';
        }
        
        return '';
    };

    // Check if a field is conditionally required based on conditional_requirements
    const isFieldConditionallyRequired = (field) => {
        if (!field.conditional_requirements) {
            return false; // No conditional requirements
        }

        const condition = field.conditional_requirements.required_when;
        if (!condition) {
            return false;
        }

        const triggerField = condition.field;
        const triggerValue = getNestedValue(formData, triggerField);
        const operator = condition.operator;
        const expectedValue = condition.value;

        switch (operator) {
            case 'equals':
                return triggerValue === expectedValue;
            case 'contains':
                return Array.isArray(triggerValue) && triggerValue.includes(expectedValue);
            case 'in':
                return Array.isArray(expectedValue) && expectedValue.includes(triggerValue);
            case 'not_equals':
                return triggerValue !== expectedValue;
            default:
                return false;
        }
    };

    // Get effective requirement status (static or conditional)
    const getEffectiveRequiredStatus = (field) => {
        // If has conditional requirements, check condition
        if (field.conditional_requirements) {
            return isFieldConditionallyRequired(field);
        }
        // Otherwise, use static is_required
        return field.is_required;
    };

    // Check if a field is required and unfilled (only show red border when there's a validation error)
    const isFieldRequiredAndUnfilled = (field) => {
        const isRequired = getEffectiveRequiredStatus(field);
        if (!isRequired) return false;
        
        // Only show red border if there's an actual validation error, not just because it's empty
        return validationErrors[field.field_key] || false;
    };

    // Check if a field is empty (for progress tracking)
    const isFieldEmpty = (field) => {
        const fieldValue = getFieldValue(field.field_key);
        return !fieldValue || fieldValue === '' || (Array.isArray(fieldValue) && fieldValue.length === 0);
    };

    const fetchAdditionalFields = async () => {
        try {
            setLoading(true);
            // Fetch ALL active fields (both system and custom)
            // System fields will be shown in Compulsory fields section
            // Custom fields will be shown in their assigned sections
            const { data, error } = await supabase
                .from('additional_registration_fields')
                .select('*')
                .or('field_status.is.null,field_status.eq.active')
                // Remove filter to include system fields for Compulsory fields section
                // .eq('is_original_field', false)  // ❌ REMOVED
                .order('section_order', { ascending: true })
                .order('display_order', { ascending: true });

            if (error) throw error;
            
            // Parse field_behaviors if it's a string (JSONB from database)
            const processedData = (data || []).map(field => {
                let fieldBehaviors = field.field_behaviors;
                if (typeof fieldBehaviors === 'string') {
                    try {
                        fieldBehaviors = JSON.parse(fieldBehaviors);
                    } catch (e) {
                        console.warn('Failed to parse field_behaviors for field:', field.field_key, e);
                        fieldBehaviors = null;
                    }
                }
                
                // If this field is missing from formData, set its default value
                const currentValue = getNestedValue(formData, field.field_key);
                if (field.default_value && (currentValue === undefined || currentValue === null || currentValue === '')) {
                    setTimeout(() => {
                        handleFieldChange(field.field_key, field.default_value);
                    }, 0);
                }

                return {
                    ...field,
                    field_behaviors: fieldBehaviors
                };
            });
            
            setAdditionalFields(processedData);
        } catch (err) {
            console.error('Error fetching additional fields:', err);
            setError('Failed to load additional fields');
        } finally {
            setLoading(false);
        }
    };

    const handleFieldChange = (fieldKey, value) => {
        console.log('handleFieldChange', fieldKey, value);
        setFormData(prevData => {
            const newData = { ...prevData };
            
            // IMPORTANT: All fields in AdditionalFields component are custom fields (is_original_field = false)
            // They should be saved to additional_data JSONB column, NOT registration table columns
            // System fields (is_original_field = true) are handled in the main registration form
            
            // Handle nested field keys (e.g., "personal_details.gender")
            if (fieldKey.includes('.')) {
                const keys = fieldKey.split('.');
                let current = newData;
                for (let i = 0; i < keys.length - 1; i++) {
                    if (!current[keys[i]]) {
                        current[keys[i]] = {};
                    }
                    current = current[keys[i]];
                }
                current[keys[keys.length - 1]] = value;
            } else {
                // For custom fields, store in additional_data structure
                // The parent component (registration form) will handle saving to additional_data
                newData[fieldKey] = value;
            }
            
            return newData;
        });

        // Clear validation error for this field
        setValidationErrors(prev => ({
            ...prev,
            [fieldKey]: false
        }));

        // Re-validate all fields with conditional requirements that depend on this field
        // This ensures that when a trigger field changes, dependent fields are re-validated
        setTimeout(() => {
            const dependentFields = additionalFields.filter(field => 
                field.conditional_requirements?.required_when?.field === fieldKey
            );
            
            dependentFields.forEach(field => {
                // Only update validation for touched fields
                if (touchedFields.has(field.field_key)) {
                    const fieldValue = getFieldValue(field.field_key);
                    const error = validateField(field, fieldValue);
                    setValidationErrors(prev => ({
                        ...prev,
                        [field.field_key]: error
                    }));
                }
            });
        }, 0);
    };

    const validateField = (field, value) => {
        // Check if field is required (static or conditional)
        const isRequired = getEffectiveRequiredStatus(field);
        
        // Check if there are staged files for this field (for file types)
        const stagedFiles = selectedFiles[field.field_key];
        const hasStagedFiles = stagedFiles && stagedFiles.length > 0;

        if (isRequired && (!value || value === '' || (Array.isArray(value) && value.length === 0)) && !hasStagedFiles) {
            return `${field.field_label} is required`;
        }

        // Passport Expiry Date Validation: Cannot be before today
        if (field.field_key === 'passport_expiry_date' && value) {
            const expiryDate = new Date(value);
            const today = new Date();
            today.setHours(0, 0, 0, 0); // Normalize today to start of day
            
            if (expiryDate < today) {
                return 'Passport has expired. Please enter a valid expiry date.';
            }
        }

        if (field.field_type === 'email' && value) {
            const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
            if (!emailRegex.test(value)) {
                return 'Please enter a valid email address';
            }
        }

        if (field.field_type === 'phone' && value) {
            const phoneRegex = /^[\+]?[1-9][\d]{0,15}$/;
            if (!phoneRegex.test(value.replace(/\s/g, ''))) {
                return 'Please enter a valid phone number';
            }
        }

        return false;
    };

    const handleFieldBlur = (field, value) => {
        // Mark field as touched
        setTouchedFields(prev => new Set(prev).add(field.field_key));
        
        // Only validate and show error after field has been touched
        const error = validateField(field, value);
        setValidationErrors(prev => ({
            ...prev,
            [field.field_key]: error
        }));
    };

    const handleFileSelection = (fieldKey, files) => {
        if (!files || files.length === 0) return;
        
        setSelectedFiles(prev => ({
            ...prev,
            [fieldKey]: files
        }));

        // Clear validation error for this field
        setValidationErrors(prev => ({
            ...prev,
            [fieldKey]: false
        }));
    };

    const handleFileUpload = async (field) => {
        const files = selectedFiles[field.field_key];
        if (!files || files.length === 0) {
            toast({
                title: "No files selected",
                status: "error",
                duration: 3000,
                isClosable: true,
            });
            return;
        }

        // Auto-save a draft registration if registrationId is missing
        if (!registrationId && onAutoSave) {
            const savedId = await onAutoSave();
            if (savedId) {
                setRegistrationId(savedId);
            } else {
                toast({
                    title: "Registration Required",
                    description: "Please fill in your basic information (name, email, etc.) first to enable file uploads.",
                    status: "warning",
                    duration: 5000,
                    isClosable: true,
                });
                return;
            }
        }
        
        // Allow uploads without registrationId for full certifications only
        const earlyUrlParams = new URLSearchParams(window.location.search);
        const earlyIsFullCertification = earlyUrlParams.get('full_certification');
        if (!registrationId && !earlyIsFullCertification) {
            toast({
                title: "Registration Required",
                description: "Please fill in your basic information (name, email, etc.) first to enable file uploads.",
                status: "warning",
                duration: 5000,
                isClosable: true,
            });
            return;
        }

        if (uploadingFiles.has(field.field_key)) {
            toast({
                title: "Please wait for the current upload to complete.",
                status: "error",
                duration: 3000,
                isClosable: true,
            });
            return;
        }

        try {
            setUploadingFiles(prev => new Set(prev).add(field.field_key));
            
            const studentName = formData.participant_name || 'Unknown';
            const sanitizedName = studentName.replace(/[^a-zA-Z0-9]/g, '_').toLowerCase();
            
            // Resolve IDs for full certification from formData first, then URL params as fallback
            const urlParams = new URLSearchParams(window.location.search);
            const isFullCertification = (formData.full_certification_id || urlParams.get('full_certification')) ? true : false;
            const fullCertificationId = formData.full_certification_id || urlParams.get('full_certification');
            const certificationRunId = formData.certification_run_id || urlParams.get('certification_run_id');
            
            console.log('File upload debug:', {
                isFullCertification,
                fullCertificationId,
                certificationRunId,
                registrationId,
                participantName: formData.participant_name
            });
            
            const uploadedFiles = [];
            const existingValue = getFieldValue(field.field_key);
            const existingFilesCount = Array.isArray(existingValue) ? existingValue.length : (existingValue ? 1 : 0);
            
            for (let i = 0; i < files.length; i++) {
                const file = files[i];
                const fileExtension = file.name.split('.').pop() || '';
                const timestamp = new Date().toISOString().split('T')[0];
                
                // Dynamic identifier for file naming - use field_behaviors if available
                let fieldIdentifier = getDynamicFileIdentifier(field);
                
                // Add index to filename to make multiple uploads unique
                // Use existingFilesCount + i + 1 to ensure uniqueness even if uploading more files later
                const fileName = `${sanitizedName}_${fieldIdentifier}_${timestamp}_${existingFilesCount + i + 1}.${fileExtension}`;
                
                // Build a stable folder path
                let folderPath;
                if (isFullCertification) {
                    // Block if IDs not ready
                    if (!fullCertificationId || !certificationRunId) {
                        toast({
                            title: "Select certification run",
                            description: "Please select a certification run before uploading files.",
                            status: "warning",
                            duration: 4000,
                            isClosable: true,
                        });
                        break;
                    }
                    // Cache once per session
                    if (!fullCertFolderPathRef.current) {
                        fullCertFolderPathRef.current = `registrations/full__cert/${fullCertificationId}/${certificationRunId}/${sanitizedName}`;
                    }
                    folderPath = fullCertFolderPathRef.current;
                } else if (registrationId) {
                    // Individual course: stable per registration
                    folderPath = `registrations/${registrationId}`;
                } else {
                    // Should not happen due to earlier guard; double safety
                    toast({
                        title: "Registration Required",
                        description: "Please fill in your name first to create a registration record for file uploads.",
                        status: "warning",
                        duration: 5000,
                        isClosable: true,
                    });
                    break;
                }
                
                console.log('Using folder path:', folderPath);
                
                const filePath = `${folderPath}/${fileName}`;
                
                const { error } = await supabase.storage
                    .from('Registration_data')
                    .upload(filePath, file, {
                        cacheControl: '3600',
                        upsert: true
                    });

                if (error) {
                    console.error('Upload error:', error);
                    toast({
                        title: `Failed to upload ${file.name}`,
                        status: "error",
                        duration: 3000,
                        isClosable: true,
                    });
                    continue;
                }

                // Store the file path instead of the public URL
                uploadedFiles.push(filePath);
            }

            if (uploadedFiles.length > 0) {
                const existingValue = getFieldValue(field.field_key);
                let finalValue;
                
                if (field.field_type === 'file_multiple') {
                    const existingFiles = Array.isArray(existingValue) ? existingValue : (existingValue ? [existingValue] : []);
                    finalValue = [...existingFiles, ...uploadedFiles];
                } else {
                    finalValue = uploadedFiles[0];
                }
                
                handleFieldChange(field.field_key, finalValue);
                
                setSelectedFiles(prev => ({
                    ...prev,
                    [field.field_key]: null
                }));
                
                const input = document.getElementById(`file-input-${field.field_key}`);
                if (input) input.value = '';
            }
        } catch (error) {
            console.error('Error uploading files:', error);
            toast({
                title: "Failed to upload files. Please try again.",
                status: "error",
                duration: 3000,
                isClosable: true,
            });
        } finally {
            setUploadingFiles(prev => {
                const newSet = new Set(prev);
                newSet.delete(field.field_key);
                return newSet;
            });
        }
    };

    const getFieldValue = (fieldKey) => {
        if (formData[fieldKey] !== undefined && formData[fieldKey] !== null && formData[fieldKey] !== '') {
            return formData[fieldKey];
        }
        
        if (fieldKey.includes('.')) {
            const keys = fieldKey.split('.');
            let current = formData;
            for (const key of keys) {
                if (current && typeof current === 'object' && key in current) {
                    current = current[key];
                } else {
                    return '';
                }
            }
            return current || '';
        }
        
        return '';
    };

    const removeUploadedFile = (fieldKey, index) => {
        const currentValue = getFieldValue(fieldKey);
        if (Array.isArray(currentValue)) {
            const newValue = currentValue.filter((_, i) => i !== index);
            handleFieldChange(fieldKey, newValue.length > 0 ? newValue : '');
        } else {
            handleFieldChange(fieldKey, '');
        }
    };


    // Render education history component
    const renderEducationHistory = (field) => {
        const options = field.field_options || ["Institution", "Qualification", "Year"];
        const jsonArrayLabels = field.field_behaviors?.json_array_labels ?? {};
        
        // Get current entries or initialize with one empty entry
        const rawEntries = getFieldValue(field.field_key);
        let educationEntries = Array.isArray(rawEntries) ? rawEntries : [];
        
        // If no entries exist, initialize with one empty entry
        if (educationEntries.length === 0) {
            const emptyEntry = {};
            options.forEach(opt => {
                emptyEntry[opt.toLowerCase().replace(/\s+/g, '_')] = '';
            });
            // Initialize with one empty entry on first render
            setTimeout(() => {
                handleFieldChange(field.field_key, [emptyEntry]);
            }, 0);
            educationEntries = [emptyEntry];
        }
        
        const addEducationEntry = () => {
            const newEntry = {};
            options.forEach(opt => {
                newEntry[opt.toLowerCase().replace(/\s+/g, '_')] = '';
            });
            const updatedEntries = [...educationEntries, newEntry];
            handleFieldChange(field.field_key, updatedEntries);
        };
        
        const updateEducationEntry = (index, fieldName, fieldValue) => {
            const updatedEntries = [...educationEntries];
            updatedEntries[index] = { ...updatedEntries[index], [fieldName]: fieldValue };
            handleFieldChange(field.field_key, updatedEntries);
        };
        
        const removeEducationEntry = (index) => {
            const updatedEntries = educationEntries.filter((_, i) => i !== index);
            handleFieldChange(field.field_key, updatedEntries);
        };
        
        return (
            <Box>
                <VStack spacing={4} align="stretch">
                    {educationEntries.map((entry, index) => (
                        <Box key={index} p={4} border="1px solid" borderColor="gray.200" borderRadius="md" bg="white">
                            <VStack spacing={4} align="stretch">
                                {options.map(option => {
                                    const fieldName = option.toLowerCase().replace(/\s+/g, '_');
                                    const displayLabel = jsonArrayLabels[fieldName] ?? option;
                                    return (
                                        <FormControl key={fieldName}>
                                            <FormLabel fontSize="xs" fontWeight="medium" color="gray.600">{displayLabel}</FormLabel>
                                            <Input
                                                value={entry[fieldName] || ''}
                                                onChange={(e) => updateEducationEntry(index, fieldName, e.target.value)}
                                                placeholder={`Enter ${displayLabel.toLowerCase()}...`}
                                                size="sm"
                                                isReadOnly={isReadOnly}
                                                bg={isReadOnly ? "gray.50" : "white"}
                                            />
                                        </FormControl>
                                    );
                                })}
                                {/* Only show remove button if there's more than 1 entry */}
                                {!isReadOnly && educationEntries.length > 1 && (
                                    <Button
                                        size="sm"
                                        onClick={() => removeEducationEntry(index)}
                                        colorScheme="red"
                                        variant="outline"
                                        leftIcon={<DeleteIcon />}
                                    >
                                        Remove
                                    </Button>
                                )}
                            </VStack>
                        </Box>
                    ))}
                </VStack>
                
                {!isReadOnly && (
                    <HStack justify="flex-start" mt={4} width="100%">
                        <Button
                            onClick={addEducationEntry}
                            size="sm"
                            colorScheme="blue"
                            variant="outline"
                            leftIcon={<AddIcon />}
                            width={{ base: '100%', md: 'auto' }}
                            whiteSpace="normal"
                            height="auto"
                            py={2}
                            px={4}
                            textAlign="center"
                            lineHeight="tall"
                        >
                            Add more Education Record / Transcript of Record (TOR)
                        </Button>
                    </HStack>
                )}
            </Box>
        );
    };

    // Render employment history component
    const renderEmploymentHistory = (field) => {
        const options = field.field_options || ["Organisation", "Position Held", "Duration"];
        const jsonArrayLabels = field.field_behaviors?.json_array_labels ?? {};
        
        // Get current entries or initialize with one empty entry
        const rawEntries = getFieldValue(field.field_key);
        let employmentEntries = Array.isArray(rawEntries) ? rawEntries : [];
        
        // If no entries exist, initialize with one empty entry
        if (employmentEntries.length === 0) {
            const emptyEntry = {};
            options.forEach(opt => {
                emptyEntry[opt.toLowerCase().replace(/\s+/g, '_')] = '';
            });
            // Initialize with one empty entry on first render
            setTimeout(() => {
                handleFieldChange(field.field_key, [emptyEntry]);
            }, 0);
            employmentEntries = [emptyEntry];
        }
        
        const addEmploymentEntry = () => {
            const newEntry = {};
            options.forEach(opt => {
                newEntry[opt.toLowerCase().replace(/\s+/g, '_')] = '';
            });
            const updatedEntries = [...employmentEntries, newEntry];
            handleFieldChange(field.field_key, updatedEntries);
        };
        
        const updateEmploymentEntry = (index, fieldName, fieldValue) => {
            const updatedEntries = [...employmentEntries];
            updatedEntries[index] = { ...updatedEntries[index], [fieldName]: fieldValue };
            handleFieldChange(field.field_key, updatedEntries);
        };
        
        const removeEmploymentEntry = (index) => {
            const updatedEntries = employmentEntries.filter((_, i) => i !== index);
            handleFieldChange(field.field_key, updatedEntries);
        };
        
        return (
            <Box>
                <VStack spacing={4} align="stretch">
                    {employmentEntries.map((entry, index) => (
                        <Box key={index} p={4} border="1px solid" borderColor="gray.200" borderRadius="md" bg="white">
                            <VStack spacing={4} align="stretch">
                                {options.map(option => {
                                    const fieldName = option.toLowerCase().replace(/\s+/g, '_');
                                    const displayLabel = jsonArrayLabels[fieldName] ?? option;
                                    return (
                                        <FormControl key={fieldName}>
                                            <FormLabel fontSize="xs" fontWeight="medium" color="gray.600">{displayLabel}</FormLabel>
                                            <Input
                                                value={entry[fieldName] || ''}
                                                onChange={(e) => updateEmploymentEntry(index, fieldName, e.target.value)}
                                                placeholder={`Enter ${displayLabel.toLowerCase()}...`}
                                                size="sm"
                                                isReadOnly={isReadOnly}
                                                bg={isReadOnly ? "gray.50" : "white"}
                                            />
                                        </FormControl>
                                    );
                                })}
                                {/* Only show remove button if there's more than 1 entry */}
                                {!isReadOnly && employmentEntries.length > 1 && (
                                    <Button
                                        size="sm"
                                        onClick={() => removeEmploymentEntry(index)}
                                        colorScheme="red"
                                        variant="outline"
                                        leftIcon={<DeleteIcon />}
                                    >
                                        Remove
                                    </Button>
                                )}
                            </VStack>
                        </Box>
                    ))}
                </VStack>
                
                {!isReadOnly && (
                    <HStack justify="flex-start" mt={4}>
                        <Button
                            onClick={addEmploymentEntry}
                            size="sm"
                            colorScheme="blue"
                            variant="outline"
                            leftIcon={<AddIcon />}
                        >
                            Add More Employment Record
                        </Button>
                    </HStack>
                )}
            </Box>
        );
    };

    const renderField = (field) => {
        const fieldValue = getFieldValue(field.field_key);
        // Only show error styling if field has been touched
        const isTouched = touchedFields.has(field.field_key);
        const fieldError = isTouched ? validationErrors[field.field_key] : null;
        const fieldId = `field-${field.id}`;

        const isRequired = getEffectiveRequiredStatus(field);
        const placeholder = getPlaceholder(field);
        const commonProps = {
            id: fieldId,
            value: fieldValue,
            onChange: (e) => handleFieldChange(field.field_key, e.target.value),
            onBlur: (e) => handleFieldBlur(field, e.target.value),
            required: isRequired,
            placeholder: placeholder || field.field_label,
            size: {base: "md", md: "md"},
            height: {base: "48px", md: "40px"}, // Larger touch target on mobile
            fontSize: {base: "16px", md: "14px"}, // Prevent zoom on iOS
            bg: isReadOnly ? "gray.50" : "white",
            isReadOnly: isReadOnly,
            isDisabled: isReadOnly,
            borderRadius: "md",
            _focus: isReadOnly ? {} : {
                borderColor: "blue.500",
                boxShadow: "0 0 0 2px rgba(66, 153, 225, 0.3)"
            }
        };

        // Searchable Select Override - use field_behaviors if available
        if (field.field_behaviors?.input_type_override === 'searchable_select' || 
            (field.field_key === 'personal_details_country_of_birth' && !field.field_behaviors)) {
            // Legacy fallback for personal_details_country_of_birth if field_behaviors not set
            const currentVal = countryOptions.find(opt => opt.label === fieldValue);
            return (
                <SearchableSelect
                    {...commonProps}
                    value={currentVal}
                    onChange={(option) => handleFieldChange(field.field_key, option ? option.label : '')}
                    onBlur={() => handleFieldBlur(field, fieldValue)}
                    options={countryOptions}
                    placeholder="Search and select Country..."
                    isInvalid={!!fieldError}
                    chakraStyles={{
                        control: (provided) => ({
                            ...provided,
                            bg: isReadOnly ? "gray.50" : "white",
                        }),
                    }}
                />
            );
        }

        switch (field.field_type) {
            case 'text':
            case 'email':
            case 'phone':
                if (field.field_type === 'phone') {
                    return (
                        <PhoneInput
                            international
                            defaultCountry="SG"
                            disabled={isReadOnly}
                            value={fieldValue || ''}
                            onChange={(value) => handleFieldChange(field.field_key, value || '')}
                            inputComponent={Input}
                            style={{
                                display: 'flex',
                                alignItems: 'center'
                            }}
                            className="chakra-phone-input"
                            inputProps={{
                                isReadOnly: isReadOnly,
                                bg: isReadOnly ? "gray.50" : "white",
                                isInvalid: fieldError,
                                id: fieldId,
                                required: isRequired,
                                placeholder: placeholder || field.field_label,
                                size: "md",
                                _focus: isReadOnly ? {} : {
                                    borderColor: "blue.500",
                                    boxShadow: "0 0 0 1px blue.500"
                                }
                            }}
                        />
                    );
                }
                
                return (
                    <Input
                        {...commonProps}
                        type={field.field_type === 'email' ? 'email' : 'text'}
                        isInvalid={fieldError}
                    />
                );

            case 'number':
                return (
                    <Input
                        {...commonProps}
                        type="number"
                        onChange={(e) => handleFieldChange(field.field_key, parseFloat(e.target.value) || 0)}
                        isInvalid={fieldError}
                    />
                );

            case 'date':
                return (
                    <Input
                        {...commonProps}
                        type="date"
                        isInvalid={fieldError}
                    />
                );

            case 'textarea':
                return (
                    <Textarea
                        {...commonProps}
                        rows={3}
                        placeholder={getPlaceholder(field)}
                        isInvalid={fieldError}
                    />
                );

            case 'boolean':
                return (
                    <Box p={3} bg={isReadOnly ? "gray.100" : "gray.50"} borderRadius="md" border="1px solid" borderColor="gray.200">
                        <HStack>
                            <input
                                type="checkbox"
                                id={fieldId}
                                checked={!!fieldValue}
                                onChange={(e) => handleFieldChange(field.field_key, e.target.checked)}
                                disabled={isReadOnly}
                                style={{ marginRight: '8px' }}
                            />
                            <Text fontSize="sm" fontWeight="medium" color="gray.700">
                                {field.field_label}
                            </Text>
                        </HStack>
                    </Box>
                );

            case 'select':
                // Check for searchable select override
                if (field.field_behaviors?.input_type_override === 'searchable_select' || 
                    (field.field_key === 'personal_details_country_of_birth' && !field.field_behaviors)) {
                    // Legacy fallback for personal_details_country_of_birth if field_behaviors not set
                    const currentVal = countryOptions.find(opt => opt.label === fieldValue);
                    return (
                        <SearchableSelect
                            {...commonProps}
                            value={currentVal}
                            onChange={(option) => handleFieldChange(field.field_key, option ? option.label : '')}
                            onBlur={() => handleFieldBlur(field, fieldValue)}
                            options={countryOptions}
                            placeholder="Search and select Country..."
                            isInvalid={!!fieldError}
                            chakraStyles={{
                                control: (provided) => ({
                                    ...provided,
                                    bg: isReadOnly ? "gray.50" : "white",
                                }),
                            }}
                        />
                    );
                }

                const options = field.field_options || [];
                const isOtherOptionSelected = activeOtherFields.has(field.field_key) || (fieldValue && !options.includes(fieldValue) && fieldValue !== '');

                // For small number of options, show as buttons for better UX on both web and mobile
                if (options.length > 0 && options.length < 5 && !field.field_behaviors?.input_type_override) {
                    return (
                        <VStack align="stretch" spacing={2}>
                            <SimpleGrid 
                                columns={{ 
                                    base: 1, 
                                    sm: options.length === 2 ? 2 : (options.length === 3 ? 3 : 2), 
                                    md: Math.min(options.length, 4) 
                                }} 
                                spacing={2}
                            >
                                {options.map((option) => {
                                    const isOthers = option === 'Others' || option === 'Other';
                                    const isSelected = isOthers ? isOtherOptionSelected : fieldValue === option;
                                    
                                    return (
                                        <Button
                                            key={option}
                                            onClick={() => {
                                                if (isOthers) {
                                                    setActiveOtherFields(prev => new Set(prev).add(field.field_key));
                                                    handleFieldChange(field.field_key, ''); // Clear value to let user type
                                                } else {
                                                    setActiveOtherFields(prev => {
                                                        const next = new Set(prev);
                                                        next.delete(field.field_key);
                                                        return next;
                                                    });
                                                    handleFieldChange(field.field_key, option);
                                                }
                                            }}
                                            colorScheme={isSelected ? "blue" : "gray"}
                                            variant={isSelected ? "solid" : "outline"}
                                            isDisabled={isReadOnly}
                                            size="md"
                                            py={{ base: 4, md: 5 }}
                                            px={4}
                                            whiteSpace="normal"
                                            height="auto"
                                            textAlign="center"
                                            fontWeight={isSelected ? "bold" : "medium"}
                                            borderColor={isSelected ? "blue.500" : "gray.300"}
                                            _hover={!isReadOnly ? {
                                                bg: isSelected ? "blue.600" : "gray.100",
                                                borderColor: "blue.500"
                                            } : {}}
                                        >
                                            {option}
                                        </Button>
                                    );
                                })}
                            </SimpleGrid>
                            {isOtherOptionSelected && !isReadOnly && (
                                <Input
                                    placeholder={placeholder || `Please specify ${field.field_label.toLowerCase()}...`}
                                    value={fieldValue === 'Others' || fieldValue === 'Other' ? '' : fieldValue}
                                    onChange={(e) => handleFieldChange(field.field_key, e.target.value)}
                                    size="md"
                                    mt={1}
                                    autoFocus
                                    borderColor="blue.300"
                                    focusBorderColor="blue.500"
                                />
                            )}
                        </VStack>
                    );
                }

                return (
                    <VStack align="stretch" spacing={2}>
                        <Select
                            {...commonProps}
                            value={isOtherOptionSelected ? 'Others' : fieldValue}
                            placeholder={placeholder || `Select ${field.field_label}`}
                            isInvalid={!!fieldError}
                            onChange={(e) => {
                                const val = e.target.value;
                                if (val === 'Others' || val === 'Other') {
                                    setActiveOtherFields(prev => new Set(prev).add(field.field_key));
                                    handleFieldChange(field.field_key, ''); // Clear value to let user type
                                } else {
                                    setActiveOtherFields(prev => {
                                        const next = new Set(prev);
                                        next.delete(field.field_key);
                                        return next;
                                    });
                                    handleFieldChange(field.field_key, val);
                                }
                            }}
                        >
                            {options.map((option) => (
                                <option key={option} value={option}>
                                    {option}
                                </option>
                            ))}
                        </Select>
                        {isOtherOptionSelected && !isReadOnly && (
                            <Input
                                placeholder={placeholder || `Please specify ${field.field_label.toLowerCase()}...`}
                                value={fieldValue === 'Others' || fieldValue === 'Other' ? '' : fieldValue}
                                onChange={(e) => handleFieldChange(field.field_key, e.target.value)}
                                size="sm"
                                mt={1}
                                autoFocus
                            />
                        )}
                    </VStack>
                );

            case 'multiselect':
                const selectedValues = Array.isArray(fieldValue) ? fieldValue : [];
                const multiselectOptions = field.field_options || [];

                // For small number of options, show as toggle buttons
                if (multiselectOptions.length > 0 && multiselectOptions.length < 5) {
                    return (
                        <SimpleGrid 
                            columns={{ 
                                base: 1, 
                                sm: multiselectOptions.length === 2 ? 2 : (multiselectOptions.length === 3 ? 3 : 2), 
                                md: Math.min(multiselectOptions.length, 4) 
                            }} 
                            spacing={2}
                        >
                            {multiselectOptions.map((option) => {
                                const isSelected = selectedValues.includes(option);
                                return (
                                    <Button
                                        key={option}
                                        onClick={() => {
                                            if (isSelected) {
                                                handleFieldChange(field.field_key, selectedValues.filter(val => val !== option));
                                            } else {
                                                handleFieldChange(field.field_key, [...selectedValues, option]);
                                            }
                                        }}
                                        colorScheme={isSelected ? "blue" : "gray"}
                                        variant={isSelected ? "solid" : "outline"}
                                        isDisabled={isReadOnly}
                                        size="md"
                                        py={{ base: 4, md: 5 }}
                                        px={4}
                                        whiteSpace="normal"
                                        height="auto"
                                        textAlign="center"
                                        fontWeight={isSelected ? "bold" : "medium"}
                                        borderColor={isSelected ? "blue.500" : "gray.300"}
                                        leftIcon={isSelected ? <Icon as={() => <span>✓</span>} /> : undefined}
                                        _hover={!isReadOnly ? {
                                            bg: isSelected ? "blue.600" : "gray.100",
                                            borderColor: "blue.500"
                                        } : {}}
                                    >
                                        {option}
                                    </Button>
                                );
                            })}
                        </SimpleGrid>
                    );
                }

                return (
                    <Box>
                        {selectedValues.length > 0 && (
                            <Box mb={3} p={3} bg="blue.50" borderRadius="md" border="1px solid" borderColor="blue.200">
                                <HStack wrap="wrap" spacing={2}>
                                    {selectedValues.map((item, index) => (
                                        <Badge key={index} colorScheme="blue" px={2} py={1} borderRadius="md">
                                            {item}
                                            {!isReadOnly && (
                                                <IconButton
                                                    size="xs"
                                                    ml={2}
                                                    icon={<DeleteIcon />}
                                                    onClick={() => {
                                                        const newValue = selectedValues.filter((_, i) => i !== index);
                                                        handleFieldChange(field.field_key, newValue);
                                                    }}
                                                    variant="ghost"
                                                    colorScheme="blue"
                                                />
                                            )}
                                        </Badge>
                                    ))}
                                </HStack>
                            </Box>
                        )}
                        <Select
                            placeholder={placeholder || `Add ${field.field_label}`}
                            onChange={(e) => {
                                const selectedValue = e.target.value;
                                if (selectedValue && !selectedValues.includes(selectedValue)) {
                                    handleFieldChange(field.field_key, [...selectedValues, selectedValue]);
                                }
                            }}
                            size="md"
                            bg={isReadOnly ? "gray.50" : "white"}
                            isDisabled={isReadOnly}
                        >
                            {multiselectOptions.filter(option => !selectedValues.includes(option)).map((option) => (
                                <option key={option} value={option}>
                                    {option}
                                </option>
                            ))}
                        </Select>
                    </Box>
                );


            case 'json_array':
                // Special handling for education and employment history
                if (field.field_key === 'education') {
                    return renderEducationHistory(field);
                } else if (field.field_key === 'employment_history' || field.field_key === 'employment_record') {
                    return renderEmploymentHistory(field);
                }
                
                return (
                    <Box>
                        <Text fontSize="sm" fontWeight="medium" mb={2} color="gray.700">
                            {field.field_label}
                            <Badge ml={2} colorScheme="yellow" size="sm">JSON Array</Badge>
                        </Text>
                        <Textarea
                            value={Array.isArray(fieldValue) ? JSON.stringify(fieldValue, null, 2) : ''}
                            onChange={(e) => {
                                try {
                                    const parsed = JSON.parse(e.target.value);
                                    handleFieldChange(field.field_key, parsed);
                                } catch {
                                    // Invalid JSON, keep as string for now
                                    handleFieldChange(field.field_key, e.target.value);
                                }
                            }}
                            rows={4}
                            fontFamily="mono"
                            fontSize="sm"
                            placeholder="Enter JSON array data..."
                            isInvalid={fieldError}
                            isReadOnly={isReadOnly}
                            bg={isReadOnly ? "gray.50" : "white"}
                        />
                    </Box>
                );

            case 'file':
            case 'file_multiple':
                const selectedFilesForField = selectedFiles[field.field_key];
                const isUploading = uploadingFiles.has(field.field_key);
                const hasSelectedFiles = selectedFilesForField && selectedFilesForField.length > 0;
                const hasUploadedFiles = fieldValue && (Array.isArray(fieldValue) ? fieldValue.length > 0 : fieldValue !== '');
                
                return (
                    <Box>
                        <input
                            type="file"
                            multiple={field.field_type === 'file_multiple'}
                            onChange={(e) => handleFileSelection(field.field_key, e.target.files)}
                            style={{ display: 'none' }}
                            id={`file-input-${field.field_key}`}
                            accept="image/*,.pdf,.doc,.docx,.txt"
                            disabled={isReadOnly}
                        />
                        
                        {/* Show Select File button even if registrationId is not present */}
                        {!isReadOnly && (
                            <Box textAlign="center" mb={hasUploadedFiles ? 4 : 0}>
                                <Button
                                    onClick={() => {
                                        if (!formData.participant_name || formData.participant_name.trim() === '') {
                                            toast({
                                                title: "Please fill in the participant name first before selecting files",
                                                status: "error",
                                                duration: 3000,
                                                isClosable: true,
                                            });
                                            return;
                                        }
                                        
                                        const input = document.getElementById(`file-input-${field.field_key}`);
                                        if (input) input.click();
                                    }}
                                    disabled={isUploading}
                                    colorScheme={hasUploadedFiles ? "blue" : "blue"}
                                    variant={hasUploadedFiles ? "solid" : "outline"}
                                    size="sm"
                                    leftIcon={<Text fontSize="lg">+</Text>}
                                >
                                {hasUploadedFiles ? 
                                    (field.field_type === 'file_multiple' ? 'Add More' : 'Change File') 
                                    : 'Select File'}
                            </Button>
                        </Box>
                        )}
                        
                        {selectedFilesForField && selectedFilesForField.length > 0 && (
                            <Box mt={4} p={3} border="1px dashed" borderColor="blue.300" borderRadius="md" bg="blue.50">
                                <Text fontSize="xs" fontWeight="bold" mb={2} color="blue.700" textTransform="uppercase">
                                    Staged for upload:
                                </Text>
                                <VStack spacing={2} align="stretch">
                                    {Array.from(selectedFilesForField).map((file, index) => (
                                        <FileAttachmentRow
                                            key={`${file.name}-${file.size}-${file.lastModified}-${index}`}
                                            file={file}
                                            fileName={file.name}
                                            variant="staged"
                                            onRemove={() => {
                                                const newFiles = Array.from(selectedFilesForField).filter((_, i) => i !== index);
                                                const dataTransfer = new DataTransfer();
                                                newFiles.forEach(f => dataTransfer.items.add(f));
                                                setSelectedFiles(prev => ({ ...prev, [field.field_key]: dataTransfer.files }));
                                            }}
                                        />
                                    ))}
                                </VStack>
                                <Text fontSize="10px" color="blue.600" mt={2} fontStyle="italic">
                                    Files will be uploaded automatically when you save.
                                </Text>
                            </Box>
                        )}
                        
                        {fieldValue && (
                            <Box mt={4}>
                                <Text fontSize="xs" fontWeight="bold" mb={2} color="green.700" textTransform="uppercase">
                                    ✓ Uploaded
                                </Text>
                                <VStack spacing={2} align="stretch">
                                    {Array.isArray(fieldValue) ? (
                                        fieldValue.map((filePath, index) => {
                                            const { data: urlData } = supabase.storage
                                                .from('Registration_data')
                                                .getPublicUrl(filePath);
                                            const fileName = filePath.split('/').pop();

                                            return (
                                                <FileAttachmentRow
                                                    key={`${filePath}-${index}`}
                                                    fileName={fileName}
                                                    previewSrc={urlData?.publicUrl}
                                                    variant="uploaded"
                                                    canRemove={!isReadOnly}
                                                    onRemove={() => removeUploadedFile(field.field_key, index)}
                                                />
                                            );
                                        })
                                    ) : (
                                        (() => {
                                            const { data: urlData } = supabase.storage
                                                .from('Registration_data')
                                                .getPublicUrl(fieldValue);
                                            return (
                                                <FileAttachmentRow
                                                    fileName={fieldValue.split('/').pop()}
                                                    previewSrc={urlData?.publicUrl}
                                                    variant="uploaded"
                                                    canRemove={!isReadOnly}
                                                    onRemove={() => removeUploadedFile(field.field_key, 0)}
                                                />
                                            );
                                        })()
                                    )}
                                </VStack>
                            </Box>
                        )}
                    </Box>
                );

            default:
                return (
                    <Input
                        {...commonProps}
                        type="text"
                        isInvalid={fieldError}
                    />
                );
        }
    };

    if (loading) {
        return (
            <Center py={8}>
                <Spinner size="lg" color="brand.600" />
            </Center>
        );
    }

    if (error) {
        return (
            <Alert status="error" borderRadius="md">
                <AlertIcon />
                {error}
            </Alert>
        );
    }

    if (additionalFields.length === 0) {
        return null;
    }

    // Helper function to get dynamic label based on field_behaviors
    const getDynamicLabel = (field) => {
        if (!field.field_behaviors?.dynamic_label) {
            return field.field_label;
        }

        const { trigger_field, mappings } = field.field_behaviors.dynamic_label;
        const triggerValue = getFieldValue(trigger_field);
        
        return mappings[triggerValue] || mappings.default || field.field_label;
    };

    // Helper function to get dynamic file identifier based on field_behaviors
    const getDynamicFileIdentifier = (field) => {
        if (!field.field_behaviors?.dynamic_file_identifier) {
            return field.field_key.replace(/\./g, '_');
        }

        const { trigger_field, mappings } = field.field_behaviors.dynamic_file_identifier;
        const triggerValue = getFieldValue(trigger_field);
        
        return mappings[triggerValue] || mappings.default || field.field_key.replace(/\./g, '_');
    };

    // Helper function to get placeholder (supports both static and dynamic)
    const getPlaceholder = (field) => {
        // First check for static custom placeholder
        if (field.field_behaviors?.custom_placeholder) {
            return field.field_behaviors.custom_placeholder;
        }
        
        // Then check for dynamic placeholder from dynamic_label.placeholders
        if (field.field_behaviors?.dynamic_label?.placeholders) {
            const { trigger_field, placeholders } = field.field_behaviors.dynamic_label;
            const triggerValue = getFieldValue(trigger_field) || '';
            
            // Try to get placeholder for current trigger value, fallback to default
            return placeholders[triggerValue] || placeholders.default || '';
        }
        
        // Fallback to empty string (no placeholder)
        return '';
    };

    // Helper function to check if a field should be visible
    const shouldShowField = (field) => {
        // Check field_behaviors first (new system)
        if (field.field_behaviors?.always_hidden === true) {
            return false;
        }

        // Legacy hardcoded checks (for backward compatibility, can be removed after migration)
        if (field.field_key === 'legal.declaration_date' ||
            field.field_key.includes('signature') ||
            field.field_key === 'sign_document_id' ||
            field.field_key === 'sign_requestid') {
            return false;
        }

        // Hide system fields from additional fields (they're rendered in main form)
        // Only show custom fields (is_original_field = false) in AdditionalFields component
        if (field.is_original_field === true) {
            return false;
        }

        // Hide custom fields in "Compulsory fields" section (they're shown in Step 1)
        // Only show custom fields NOT in "Compulsory fields" section in Step 2
        if (field.section === 'Compulsory fields') {
            return false;
        }

        // If field is always required, show it
        if (field.is_required) {
            return true;
        }

        // If field has no conditional requirements, show it (it's optional)
        if (!field.conditional_requirements) {
            return true;
        }

        // If field has always_visible flag, show it regardless of condition
        // (it will be optional when condition is not met, required when condition is met)
        if (field.conditional_requirements.always_visible === true) {
            return true;
        }

        // If field has conditional requirements, only show if condition is met
        return getEffectiveRequiredStatus(field);
    };

    // Group fields by section and track section_order
    // IMPORTANT: Only custom fields (is_original_field = false) are shown here
    // System fields (is_original_field = true) are rendered in the main registration form
    // Custom fields moved to "Compulsory fields" section will appear here and save to additional_data
    const sectionOrderMap = {}; // Track section_order for each section
    const groupedFields = additionalFields
        .filter(shouldShowField)
        .reduce((acc, field) => {
            const originalSection = field.section || 'Additional Information';
            const simplifiedSection = mapToSimplifiedSection(originalSection);
            if (!acc[simplifiedSection]) {
                acc[simplifiedSection] = [];
            }
            acc[simplifiedSection].push(field);
            
            // Track the section_order (use the first field's section_order for each section)
            if (sectionOrderMap[simplifiedSection] === undefined) {
                sectionOrderMap[simplifiedSection] = field.section_order;
            }
            
            return acc;
        }, {});

    // Sort sections by section_order from database
    // "Compulsory fields" should always be first if it exists
    const finalSectionOrder = Object.keys(groupedFields).sort((a, b) => {
        // Compulsory fields always comes first
        if (a === 'Compulsory fields') return -1;
        if (b === 'Compulsory fields') return 1;
        
        const orderA = sectionOrderMap[a];
        const orderB = sectionOrderMap[b];
        
        // Handle NULL/undefined values - put them at the end
        if (orderA === null || orderA === undefined) return 1;
        if (orderB === null || orderB === undefined) return -1;
        
        // Sort by section_order ascending
        return orderA - orderB;
    });

    // Calculate overall progress (only count visible fields)
    const allFields = additionalFields.filter(shouldShowField);
    const completedOverall = allFields.filter(field => !isFieldEmpty(field)).length;
    const totalOverall = allFields.length;
    const progressPercentage = totalOverall > 0 ? Math.round((completedOverall / totalOverall) * 100) : 0;

    return (
        <Box>
            {/* All sections always expanded - no accordion collapsing */}
            <VStack spacing={{base: 4, md: 5}} align="stretch">
                {finalSectionOrder.map((sectionName) => {
                    const fields = groupedFields[sectionName];
                    if (!fields) return null;
                    const config = getSectionConfig(sectionName);
                    const isCompleted = fields.filter(field => !isFieldEmpty(field)).length === fields.length;
                    
                    return (
                        <Box 
                            key={sectionName} 
                            border="1px solid" 
                            borderColor={isCompleted ? "green.300" : config.borderColor} 
                            borderRadius="lg" 
                            overflow="hidden"
                            boxShadow={isCompleted ? "0 2px 8px rgba(72, 187, 120, 0.15)" : "sm"}
                            transition="all 0.2s"
                            bg="white"
                        >
                            {/* Section Header - Always visible, no expand/collapse */}
                            <Box 
                                bg={isCompleted ? "green.50" : config.bgColor}
                                py={{base: 3, md: 4}}
                                px={{base: 3, md: 5}}
                                borderBottom="1px solid"
                                borderColor={isCompleted ? "green.200" : config.borderColor}
                            >
                                <HStack spacing={{base: 2, md: 4}}>
                                    <Box 
                                        fontSize={{base: "xl", md: "2xl"}}
                                        p={{base: 1.5, md: 2}}
                                        bg="white" 
                                        borderRadius="md" 
                                        boxShadow="sm"
                                        minW={{base: "36px", md: "44px"}}
                                        textAlign="center"
                                    >
                                        {config.icon}
                                    </Box>
                                    <HStack spacing={2} flex="1" flexWrap="wrap">
                                        <Text 
                                            fontSize={{base: "sm", md: "md"}} 
                                            fontWeight="bold" 
                                            color={isCompleted ? "green.700" : `${config.color}.700`}
                                        >
                                            {sectionName}
                                        </Text>
                                        {isCompleted && (
                                            <Badge 
                                                colorScheme="green" 
                                                variant="solid" 
                                                px={1.5} 
                                                py={0.5} 
                                                fontSize="10px"
                                            >
                                                ✓ Complete
                                            </Badge>
                                        )}
                                    </HStack>
                                </HStack>
                            </Box>
                            
                            {/* Section Fields - Always shown */}
                            <Box 
                                pb={{base: 4, md: 6}} 
                                px={{base: 3, md: 5}} 
                                pt={{base: 3, md: 4}}
                                bg="white"
                            >
                                <VStack spacing={{base: 3, md: 4}} align="stretch">
                                    {fields.map((field) => {
                                        const isRequired = getEffectiveRequiredStatus(field);
                                        const fieldLabel = getDynamicLabel(field);

                                        // Only show errors for touched fields (user has interacted with them)
                                        const showError = touchedFields.has(field.field_key) && validationErrors[field.field_key];
                                        
                                        return (
                                            <FormControl key={field.id} isInvalid={showError}>
                                                <FormLabel 
                                                    mb={{base: 1.5, md: 2}} 
                                                    fontWeight="bold" 
                                                    color="gray.800" 
                                                    fontSize={{base: "sm", md: "md"}}
                                                >
                                                    {fieldLabel}
                                                    {isRequired && <Text as="span" color="red.500" ml={1}>*</Text>}
                                                </FormLabel>
                                                {renderField(field)}
                                                {showError && (
                                                    <Text color="red.500" fontSize={{base: "xs", md: "sm"}} mt={1}>
                                                        {validationErrors[field.field_key]}
                                                    </Text>
                                                )}
                                            </FormControl>
                                        );
                                    })}
                                </VStack>
                            </Box>
                        </Box>
                    );
                })}
            </VStack>
        </Box>
    );
};

export default AdditionalFields;